import { useQuery } from "react-apollo";
import { specialistTypeReferenceQuery } from "../../../__generated__/listings";
import { SPECIALIST_TYPE_REFERENCE_QUERY } from "../../common/queries";
import * as React from "react";
import { get, sortBy } from "lodash";
import { AREA } from "./constant";
import { PremiumBalanceTooltip } from "../selfServe/components/Tooltip";
import { Icon } from "@customer-tools-web/component-ui/lib";
import { PremiumBalanceTitle } from "../selfServe/components/SpecialistSummary.style";
import { valueNotNull } from "../../common/tools";
import featureToggles from "../../../config/feature-toggles.malaysia";

type Options = { value: number | string; label: string }[];
type Translation = (key: string, args?: object) => string;

export const isCanAccessSpecialist = (userId?: string): boolean => {
  if (featureToggles.AREA_CONDO_SPECIALIST_SELF_SERVE) {
    return true;
  }
  if (!userId) {
    return false;
  }
  const demoUserIds =
    process.env.REACT_APP_FEATURE_AREA_CONDO_SPECIALIST_SELF_SERVE_DEMO_USER_IDS?.split(
      ","
    ) || [];
  return demoUserIds.includes(userId);
};

export const useSpecialistTypeOptions = (): Options => {
  const { data } = useQuery<specialistTypeReferenceQuery>(
    SPECIALIST_TYPE_REFERENCE_QUERY
  );
  return React.useMemo(() => {
    if (data) {
      const tabOptions = sortBy(
        get(data, "references.specialistType", []),
        "description"
      );
      return tabOptions.map((data: any) => {
        return {
          value: data?.code,
          label: data?.label,
        };
      });
    } else {
      return [];
    }
  }, [data]);
};

export const getSortingOptions = (t: Translation): Options => {
  return [
    {
      label: t("specialist.sorting.new-to-old"),
      value: "new-to-old",
    },
    {
      label: t("specialist.sorting.old-to-new"),
      value: "old-to-new",
    },
    {
      label: t("specialist.sorting.end_date_new-to-old"),
      value: "end_date_new-to-old",
    },
    {
      label: t("specialist.sorting.end_date_old-to-new"),
      value: "end_date_old-to-new",
    },
  ];
};

export const useSelfServeTableColumns = (
  t: Translation,
  typeCode: number,
  isLessThanTablet: boolean,
  isLoginAsAgent: boolean
) =>
  React.useMemo(() => {
    if (isLessThanTablet) {
      return [
        {
          dataIndex: "summary",
          key: "summary",
        },
      ];
    }

    const premiumBalanceColumn = (
      <span className={"action"}>
        {t("specialist.premiumUpgrade")}
        <PremiumBalanceTooltip />
      </span>
    );

    if (typeCode === AREA) {
      return [
        isLoginAsAgent
          ? {
              title: t("specialist.specialistId"),
              dataIndex: "id",
              key: "id",
            }
          : undefined,
        {
          title: t("specialist.city"),
          dataIndex: "city",
          key: "city",
        },
        {
          title: t("specialist.state"),
          dataIndex: "state",
          key: "state",
        },
        {
          title: t("specialist.status"),
          dataIndex: "status",
          key: "status",
        },
        {
          title: t("specialist.categoryType"),
          dataIndex: "categoryType",
          key: "categoryType",
        },
        {
          title: t("specialist.startDate"),
          dataIndex: "startDate",
          key: "startDate",
        },
        {
          title: t("specialist.endDate"),
          dataIndex: "endDate",
          key: "endDate",
        },
        {
          title: t("specialist.duration"),
          dataIndex: "duration",
          key: "duration",
          width: 95,
        },
        {
          title: premiumBalanceColumn,
          dataIndex: "btnRedeem",
          key: "btnRedeem",
        },
        {
          title: t("specialist.subscription"),
          dataIndex: "btnExtend",
          key: "btnExtend",
        },
      ].filter(valueNotNull);
    } else {
      return [
        isLoginAsAgent
          ? {
              title: t("specialist.specialistId"),
              dataIndex: "id",
              key: "id",
            }
          : undefined,
        {
          title: t("specialist.building"),
          dataIndex: "building",
          key: "building",
        },
        {
          title: t("specialist.city"),
          dataIndex: "city",
          key: "city",
        },
        {
          title: t("specialist.status"),
          dataIndex: "status",
          key: "status",
        },
        {
          title: t("specialist.startDate"),
          dataIndex: "startDate",
          key: "startDate",
        },
        {
          title: t("specialist.endDate"),
          dataIndex: "endDate",
          key: "endDate",
        },
        {
          title: t("specialist.duration"),
          dataIndex: "duration",
          key: "duration",
          width: 95,
        },
        {
          title: premiumBalanceColumn,
          dataIndex: "btnRedeem",
          key: "btnRedeem",
        },
        {
          title: t("specialist.subscription"),
          dataIndex: "btnExtend",
          key: "btnExtend",
        },
      ].filter(valueNotNull);
    }
  }, [typeCode, isLessThanTablet]);

export const useSpecialistSummaryTableColumns = (
  t: Translation,
  isLessThanTablet: boolean,
  isFullInfo: boolean = false
) =>
  React.useMemo(() => {
    const areaTitle = (
      <span className={"title"}>
        <Icon size={"14px"} type={"area-i"} /> {t("specialistSelfServe.type.2")}
      </span>
    );
    const condoTitle = (
      <span className={"title"}>
        <Icon size={"14px"} type={"condo-i"} />{" "}
        {t("specialistSelfServe.type.1")}
      </span>
    );

    const premiumBalanceColumn = (
      <PremiumBalanceTitle>
        <span>{t("specialist.premiumUpgrade")}</span>
        <PremiumBalanceTooltip iconColor={"#333F48"} />
      </PremiumBalanceTitle>
    );

    if (isLessThanTablet) {
      return {
        area: [
          {
            dataIndex: "summary",
            key: "summary",
          },
        ],
        condo: [
          {
            dataIndex: "summary",
            key: "summary",
          },
        ],
      };
    }

    return {
      area: [
        {
          title: areaTitle,
          dataIndex: "city",
          key: "city",
        },
        {
          title: t("specialist.status"),
          dataIndex: "status",
          key: "status",
          width: 100,
        },
        {
          title: t("specialist.startDate"),
          dataIndex: "startDate",
          key: "startDate",
          width: 100,
        },
        {
          title: t("specialist.endDate"),
          dataIndex: "expiryDate",
          key: "expiryDate",
          width: 100,
        },
        isFullInfo
          ? {
              title: premiumBalanceColumn,
              dataIndex: "premiumBalance",
              key: "premiumBalance",
              width: 80,
            }
          : undefined,
      ].filter(valueNotNull),
      condo: [
        {
          title: condoTitle,
          dataIndex: "building",
          key: "building",
        },
        {
          title: t("specialist.status"),
          dataIndex: "status",
          key: "status",
          width: 100,
        },
        {
          title: t("specialist.startDate"),
          dataIndex: "startDate",
          key: "startDate",
          width: 100,
        },
        {
          title: t("specialist.endDate"),
          dataIndex: "expiryDate",
          key: "expiryDate",
          width: 100,
        },
        isFullInfo
          ? {
              title: premiumBalanceColumn,
              dataIndex: "premiumBalance",
              key: "premiumBalance",
              width: 80,
            }
          : undefined,
      ].filter(valueNotNull),
    };
  }, [isLessThanTablet]);
